import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

// Style
import {
  LanguagesList,
  LanguageText,
  LanguageSymbol,
  Trigger,
  TriggerIcon,
  LanguageContainer,
  LanguageSelected,
  SymbolLanguageSelected,
  LanguageSelectedContainer,
  LanguagesWrapper,
  LanguageFlagContainer
} from "./Languages.style";
import { Dropdown, DropdownPurpleItem } from "components/spocketUI";

// Constants
import { APP_ASSETS_BUCKET_URL } from "constants/common";

// Utils
import { getUserLanguageBasedOnUrlParams } from "utils/i18n/languageUtils";

// Assets
const iconExpand = `${APP_ASSETS_BUCKET_URL}/icon-expand.svg`;
const languageIcon = `${APP_ASSETS_BUCKET_URL}/language_icon.svg`;
const flagDe = `${APP_ASSETS_BUCKET_URL}/language-flag-de.svg`;
const flagEn = `${APP_ASSETS_BUCKET_URL}/language-flag-en.svg`;
const flagPt = `${APP_ASSETS_BUCKET_URL}/language-flag-pt-br.svg`;
const flagEs = `${APP_ASSETS_BUCKET_URL}/language-flag-es.svg`;
const flagFr = `${APP_ASSETS_BUCKET_URL}/language-flag-fr.svg`;
const flagIt = `${APP_ASSETS_BUCKET_URL}/language-flag-it.svg`;

const supportedLanguages = {
  de: {
    name: "German",
    image: flagDe
  },
  en: {
    name: "English",
    image: flagEn
  },
  fr: {
    name: "French",
    image: flagFr
  },
  it: {
    name: "Italian",
    image: flagIt
  },
  pt: {
    name: "Portuguese",
    image: flagPt
  },
  es: {
    name: "Spanish",
    image: flagEs
  }
};

const getLanguageToUse = () => {
  const userLanguage = getUserLanguageBasedOnUrlParams();
  return !isEmpty(userLanguage) ? userLanguage : false;
};

const renderDropdownOptions = ({ selectedLanguage, languages, onUpdateClickLanguage }) => {
  return Object.keys(languages).map(language => {
    const isSelected = language.toLocaleLowerCase() === (selectedLanguage || "").toLowerCase();
    const fullLanguage = languages[language];
    return (
      <DropdownPurpleItem
        data-heap="languages-list-options"
        onClick={() => onUpdateClickLanguage(language)}
        key={fullLanguage.name}
      >
        <>
          <LanguageFlagContainer>
            <img src={fullLanguage.image} alt="dropdown checkmark" />
          </LanguageFlagContainer>
          {isSelected ? (
            <LanguageSelectedContainer>
              <LanguageSelected>{fullLanguage.name}</LanguageSelected>
              <SymbolLanguageSelected>({language})</SymbolLanguageSelected>
            </LanguageSelectedContainer>
          ) : (
            fullLanguage.name
          )}
        </>
      </DropdownPurpleItem>
    );
  });
};

export const Languages = () => {
  const { i18n } = useTranslation();
  const [clickedLanguage, setClickedLanguage] = useState("");

  useEffect(() => {
    const userLanguage = !isEmpty(getLanguageToUse()) ? getLanguageToUse() : i18n.language;
    setClickedLanguage(userLanguage);
    localStorage.setItem("language", userLanguage);
    i18n.changeLanguage(userLanguage);
  }, [i18n.language]);

  if (isEmpty(clickedLanguage)) {
    return null;
  }

  const getUserLanguage = () => {
    if (clickedLanguage) {
      return supportedLanguages[clickedLanguage].name;
    }
    return supportedLanguages[i18n.language].name;
  };

  const getLanguageSymbol = () => {
    const language = supportedLanguages[clickedLanguage];
    return Object.keys(supportedLanguages).find(key => supportedLanguages[key] === language);
  };

  const updateOnClickLanguage = language => {
    setClickedLanguage(language);
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
  };

  return (
    <LanguagesWrapper data-heap="language-option-selected">
      <LanguagesList position="right">
        <Dropdown.Trigger>
          <Trigger data-heap="account-language-dropdown">
            <TriggerIcon src={languageIcon} alt="languages list" />
            <LanguageContainer>
              <LanguageText>{getUserLanguage()}</LanguageText>
              <LanguageSymbol>({getLanguageSymbol()})</LanguageSymbol>
            </LanguageContainer>
            <TriggerIcon src={iconExpand} alt="expand" />
          </Trigger>
        </Dropdown.Trigger>
        <Dropdown.Content minWidth="fill-available">
          {renderDropdownOptions({
            selectedLanguage: clickedLanguage,
            languages: supportedLanguages,
            onUpdateClickLanguage: updateOnClickLanguage
          })}
        </Dropdown.Content>
      </LanguagesList>
    </LanguagesWrapper>
  );
};
